import { type FormControlProps, FormHelperText, Grid } from "@mui/material";
import { uniqBy } from "lodash-es";
import type { FC } from "react";
import {
  type FieldError,
  type FieldErrorsImpl,
  FormProvider,
  type Merge,
  useFormContext,
} from "react-hook-form";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import type { Segment } from "../../segment/types";
import { ScheduleEventSelector } from "./schedule-form-v2-layout/schedule-event-selector";
import { ScheduleFormV2Layout } from "./schedule-form-v2-layout/schedule-form-v2-layout";

export type ScheduleFormProps = FormControlProps & {
  showValidationErrors: boolean;
  disabled: boolean;
  reloadMergeTags: () => void;
};

export const ScheduleForm: FC<ScheduleFormProps> = ({
  disabled,
  showValidationErrors,
  reloadMergeTags,
}) => {
  const methods = useFormContext<MomentFormMoment>();

  const {
    formState: { touchedFields, errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <Grid size={{ xs: 12 }}>
        <ScheduleEventSelector disabled={disabled} reloadMergeTags={reloadMergeTags} />
      </Grid>

      <ScheduleFormV2Layout disabled={disabled} showValidationErrors={showValidationErrors} />

      <Grid container spacing={2}>
        {showValidationErrors ||
        (typeof touchedFields.schedule !== "boolean" &&
          (touchedFields.schedule?.event ||
            touchedFields.schedule?.event_repeat ||
            touchedFields.schedule?.interval_json ||
            touchedFields.schedule?.send_at))
          ? uniqBy(
              Object.entries(errors.schedule ?? {})
                .filter(([k, v]) => v && !["events", "on_event_key"].includes(k))
                .map(([, v]) => v as Merge<FieldError, FieldErrorsImpl<Segment>>),
              "message",
            ).map((error, index) => <ErrMessage key={index} message={error.message} />)
          : null}
      </Grid>
    </FormProvider>
  );
};

const ErrMessage: FC<{
  message?:
    | string
    | FieldError
    | Partial<{ type: string | number; message: string }>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | Merge<FieldError, FieldErrorsImpl<any>>;
}> = ({ message }) => {
  if (!message) {
    return null;
  }

  if (typeof message === "string") {
    return (
      <FormHelperText error variant="filled">
        {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
        <span dangerouslySetInnerHTML={{ __html: message }} />
      </FormHelperText>
    );
  }

  if (message.message) return <ErrMessage message={message.message} />;

  return null;
};
